/**
 * @fileoverview Section "Domain inputs" — inputs métier GNPC.
 *
 * Ces composants encapsulent la logique de saisie propre au domaine
 * (téléphone CI, format plaque, ULID, OTP à 6 chiffres...).
 * Chaque composant expose une valeur normalisée (E.164 pour le téléphone)
 * qui peut être envoyée directement à l'API GNPC.
 *
 * Livrés à ce jour : `PhoneNumberInput`, `OtpInput`, `TotpInput`, `QrCode`.
 * À livrer : `PlateInput`, `AmountInput`, `LiquidationNumber`,
 * `MerchantReference` (voir catalogue).
 */

import { useState } from 'react';
import { FormField, OtpInput, PhoneNumberInput, QrCode, TotpInput } from '@gnpc/ui';
import { DsPreview } from '../components/DsPreview.tsx';

const MFA_OTPAUTH_URI =
  'otpauth://totp/GNPC:agent@quipux.ci?secret=JBSWY3DPEHPK3PXP&issuer=GNPC&algorithm=SHA1&digits=6&period=30';

export function DsSectionDomainInputs(): JSX.Element {
  return (
    <div className="space-y-8">
      <div className="border-quipux-orange-200 bg-quipux-orange-50 text-quipux-navy-900 rounded-lg border p-4 text-xs">
        <p className="text-quipux-orange-700 mb-1 font-semibold">Convention</p>
        <p>
          Chaque composant expose une <em>valeur canonique</em> normalisée pour l'API :
          <br />- <code>PhoneNumberInput</code> → <code>+225XXXXXXXXXX</code> (E.164, 13 car.).
          <br />- <code>OtpInput</code> / <code>TotpInput</code> → six chiffres, sans espaces.
          <br />- <code>QrCode</code> → SVG scannable pour les URI d'enrôlement MFA.
        </p>
      </div>

      <div className="space-y-3">
        <h3 className="text-quipux-navy-950 text-sm font-semibold">
          PhoneNumberInput — téléphone Côte d'Ivoire + drapeau CI
        </h3>
        <div className="grid gap-4 lg:grid-cols-2">
          <DsPreview label="Interactif — l'onChange n'émet qu'après 10 chiffres">
            <PhoneNumberDemo />
          </DsPreview>
          <DsPreview label="Trois tailles alignées">
            <div className="flex w-full flex-col gap-3">
              <PhoneNumberInput size="sm" />
              <PhoneNumberInput size="md" />
              <PhoneNumberInput size="lg" />
            </div>
          </DsPreview>
          <DsPreview label="Valeur pré-remplie (E.164)">
            <div className="w-full">
              <FormField
                label="Téléphone du compte Quipux"
                required
                tooltip="Format E.164 pour l'API GNPC. Le numéro doit être lié au compte Quipux."
                hint="Ce numéro recevra le code OTP par SMS."
              >
                <PhoneNumberInput value="+2250700000042" />
              </FormField>
            </div>
          </DsPreview>
          <DsPreview label="État désactivé">
            <div className="w-full">
              <PhoneNumberInput value="+2250700000042" disabled />
            </div>
          </DsPreview>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-quipux-navy-950 text-sm font-semibold">
          OtpInput — code SMS à 6 chiffres
        </h3>
        <div className="grid gap-4 lg:grid-cols-2">
          <DsPreview label="Interactif — focus auto + collage du code complet">
            <OtpDemo />
          </DsPreview>
          <DsPreview label="États — erreur, disabled, tailles">
            <div className="w-full space-y-4">
              <FormField
                label="Code OTP"
                error="Code invalide ou expiré. Demandez un nouveau code."
              >
                <OtpInput value="1204" aria-invalid />
              </FormField>
              <OtpInput value="123456" disabled size="sm" />
              <OtpInput value="987654" size="lg" />
            </div>
          </DsPreview>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-quipux-navy-950 text-sm font-semibold">
          MFA Google Authenticator — QR code + TotpInput
        </h3>
        <div className="grid gap-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          <DsPreview label="QrCode — URI otpauth scannable" align="center">
            <QrCode
              value={MFA_OTPAUTH_URI}
              label="Scanner avec Google Authenticator"
              description="Enrôlement MFA agent backoffice GNPC."
            />
          </DsPreview>
          <DsPreview label="TotpInput — code MFA + bascule code de secours">
            <TotpDemo />
          </DsPreview>
        </div>
      </div>

      <div className="border-border bg-card text-muted-foreground rounded-lg border border-dashed p-6 text-sm">
        <p className="text-quipux-navy-900 mb-2 font-medium">Backlog</p>
        <ul className="list-inside list-disc space-y-1 text-xs">
          <li>
            <code>PlateInput</code> — masque plaque CI, uppercase auto (AB 572 GG)
          </li>
          <li>
            <code>AmountInput</code> — XOF entier, séparateurs milliers, bornes 300 / 2 M
          </li>
          <li>
            <code>LiquidationNumber</code> — LIQ-XXXXXX monospace + copy
          </li>
          <li>
            <code>MerchantReference</code> — ULID 26 car. monospace + copy
          </li>
        </ul>
      </div>
    </div>
  );
}

function OtpDemo(): JSX.Element {
  const [value, setValue] = useState('');

  return (
    <div className="w-full space-y-3">
      <FormField
        label="Code reçu par SMS"
        required
        tooltip="Code à usage unique envoyé au téléphone du compte Quipux. Le collage d'un code complet est supporté."
        hint="Essayez de coller 123456 dans la première case."
      >
        <OtpInput value={value} onChange={setValue} />
      </FormField>
      <div className="bg-muted text-quipux-navy-800 rounded-md p-2 font-mono text-[11px]">
        <span className="text-muted-foreground">Valeur canonique : </span>
        {value || <span className="text-muted-foreground italic">— vide —</span>}
      </div>
    </div>
  );
}

function TotpDemo(): JSX.Element {
  const [value, setValue] = useState('');
  const [mode, setMode] = useState<'totp' | 'recovery'>('totp');
  const hint =
    mode === 'totp'
      ? "Saisir les 6 chiffres affichés dans l'application."
      : "Saisir un code de secours fourni à l'enrôlement.";

  return (
    <div className="w-full space-y-3">
      <FormField
        label={mode === 'totp' ? 'Code MFA' : 'Code de récupération'}
        required
        tooltip="Le message d'échec restera identique quel que soit le motif pour éviter l'énumération."
        hint={hint}
      >
        <TotpInput
          value={value}
          onChange={setValue}
          issuerLabel={
            mode === 'totp' ? 'Google Authenticator · période 30 s' : 'Mode récupération'
          }
          recoveryActionLabel={
            mode === 'totp' ? 'Utiliser un code de secours' : 'Revenir au code MFA'
          }
          onRecoveryAction={() => {
            setValue('');
            setMode((current) => (current === 'totp' ? 'recovery' : 'totp'));
          }}
        />
      </FormField>
      <div className="bg-muted text-quipux-navy-800 rounded-md p-2 font-mono text-[11px]">
        <span className="text-muted-foreground">Mode : </span>
        {mode}
        <span className="text-muted-foreground"> · Valeur : </span>
        {value || '—'}
      </div>
    </div>
  );
}

/**
 * Petite démo contrôlée : affiche la valeur E.164 en dessous du champ
 * pour prouver que `onChange` ne se déclenche qu'à 10 chiffres.
 */
function PhoneNumberDemo(): JSX.Element {
  const [value, setValue] = useState('');
  return (
    <div className="w-full space-y-2">
      <FormField
        label="Téléphone citoyen"
        required
        tooltip="Numéro Côte d'Ivoire lié au compte Quipux. Format normalisé à l'envoi."
        hint="Composez le numéro national."
      >
        <PhoneNumberInput value={value} onChange={setValue} />
      </FormField>
      <div className="bg-muted text-quipux-navy-800 rounded-md p-2 font-mono text-[11px]">
        <span className="text-muted-foreground">onChange E.164 : </span>
        {value || (
          <span className="text-muted-foreground italic">
            — chaîne vide tant que &lt; 10 chiffres —
          </span>
        )}
      </div>
    </div>
  );
}
